/** Shared domain types for the mic assignment tool. */
/**
 * Body-worn mics must be physically taken off one presenter and fitted to the
 * next, which costs real minutes backstage. A handheld is passed across in
 * seconds, and a lectern mic never moves at all -- presenters walk up to it.
 * Only body-worn types incur the changeover buffer.
 */
export const BODY_WORN = ['lav', 'headset'];
export const isBodyWorn = (t) => BODY_WORN.includes(t);
export const MIC_TYPE_LABEL = {
    lav: 'Lavalier (LAV)',
    headset: 'Headset (HS)',
    handheld: 'Handheld (HH)',
    lectern: 'Lectern (LEC)',
};
/** Short tag for dense UI. */
export const MIC_TYPE_SHORT = {
    lav: 'LAV', headset: 'HS', handheld: 'HH', lectern: 'LEC',
};
export const MIC_TYPES = ['lav', 'headset', 'handheld', 'lectern'];
export const SESSION_MODES = ['run-and-gun', 'set-list', 'timed'];
export const SESSION_MODE_LABEL = {
    'run-and-gun': 'Run and gun',
    'set-list': 'Set list',
    timed: 'Timed',
};
export const SESSION_MODE_HINT = {
    'run-and-gun': 'No schedule. Assign mics as you go; the board is the truth.',
    'set-list': 'An ordered running order. Advance item by item; each cue carries its own assignments.',
    timed: 'Clock times. Presenters have on-stage windows and the timeline is the plan.',
};
/** Stand-ins for a photo: quicker to set and legible at a glance. */
export const PRESENTER_ICONS = [
    'star', 'mic', 'guitar', 'keys', 'drums', 'horn', 'violin', 'voice',
    'crown', 'mask', 'podium', 'note',
];
export const CUE_KINDS = ['number', 'speech', 'other'];
export const CUE_KIND_LABEL = {
    number: 'Musical number',
    speech: 'Speech',
    other: 'Other',
};
export const ENSEMBLES = ['solo', 'small-group', 'ensemble'];
export const ENSEMBLE_LABEL = {
    solo: 'Solo',
    'small-group': 'Small group',
    ensemble: 'Ensemble',
};
export const DEFAULT_SETTINGS = {
    changeoverBufferMin: 15,
    assignPriority: 'spread',
    autoDeploy: false,
    deployNowBehavior: 'disable-autodeploy',
};
/**
 * Preset messages.
 *
 * A1 and A2 are on headsets but frequently cannot talk -- the band is loud, a
 * mic is open, or the note is for two minutes from now. These are the things
 * that actually get said between positions, one tap each.
 */
export const MESSAGE_PRESETS = [
    { text: 'Standby' },
    { text: 'Go' },
    { text: 'Mic is open' },
    { text: 'Mute me' },
    { text: 'Battery low', urgent: true },
    { text: 'RF dropout', urgent: true },
    { text: 'Swap the pack', urgent: true },
    { text: 'Changeover now' },
    { text: 'Presenter is not miked', urgent: true },
    { text: 'Hold the next cue' },
    { text: 'Copy that' },
    { text: 'All good' },
];
export const CUE_TEMPLATES = [
    {
        id: 'two-act-play',
        name: 'Two-act play',
        description: 'Curtain, two acts either side of an interval, and a call.',
        items: [
            { label: 'House open', kind: 'other', offset: -30, minutes: 30 },
            { label: 'Act I', kind: 'speech', ensemble: 'ensemble', offset: 0, minutes: 55 },
            { label: 'Interval', kind: 'other', offset: 55, minutes: 20 },
            { label: 'Act II', kind: 'speech', ensemble: 'ensemble', offset: 75, minutes: 50 },
            { label: 'Curtain call', kind: 'other', ensemble: 'ensemble', offset: 125, minutes: 5 },
        ],
    },
    {
        id: 'musical',
        name: 'Musical',
        description: 'Overture, numbers and book scenes across two acts.',
        items: [
            { label: 'House open', kind: 'other', offset: -30, minutes: 30 },
            { label: 'Overture', kind: 'number', ensemble: 'ensemble', offset: 0, minutes: 5 },
            { label: 'Opening number', kind: 'number', ensemble: 'ensemble', offset: 5, minutes: 8 },
            { label: 'Scene 1', kind: 'speech', ensemble: 'small-group', offset: 13, minutes: 12 },
            { label: 'Solo', kind: 'number', ensemble: 'solo', offset: 25, minutes: 5 },
            { label: 'Act I finale', kind: 'number', ensemble: 'ensemble', offset: 30, minutes: 10 },
            { label: 'Interval', kind: 'other', offset: 40, minutes: 20 },
            { label: 'Act II opener', kind: 'number', ensemble: 'ensemble', offset: 60, minutes: 8 },
            { label: 'Duet', kind: 'number', ensemble: 'small-group', offset: 68, minutes: 5 },
            { label: 'Finale', kind: 'number', ensemble: 'ensemble', offset: 73, minutes: 10 },
            { label: 'Curtain call', kind: 'other', ensemble: 'ensemble', offset: 83, minutes: 5 },
        ],
    },
    {
        id: 'band',
        name: 'Band set',
        description: 'A support act, a changeover and a headline set.',
        items: [
            { label: 'Doors', kind: 'other', offset: -60, minutes: 60 },
            { label: 'Support set', kind: 'number', ensemble: 'ensemble', offset: 0, minutes: 30 },
            { label: 'Changeover', kind: 'other', offset: 30, minutes: 20 },
            { label: 'Headline set', kind: 'number', ensemble: 'ensemble', offset: 50, minutes: 75 },
            { label: 'Encore', kind: 'number', ensemble: 'ensemble', offset: 125, minutes: 10 },
        ],
    },
    {
        id: 'corporate',
        name: 'Corporate event',
        description: 'Welcome, keynote, a panel and closing remarks.',
        items: [
            { label: 'Doors and walk-in', kind: 'other', offset: -30, minutes: 30 },
            { label: 'Welcome', kind: 'speech', ensemble: 'solo', offset: 0, minutes: 10 },
            { label: 'Keynote', kind: 'speech', ensemble: 'solo', offset: 10, minutes: 40 },
            { label: 'Panel', kind: 'speech', ensemble: 'small-group', offset: 50, minutes: 45 },
            { label: 'Break', kind: 'other', offset: 95, minutes: 20 },
            { label: 'Breakout session', kind: 'speech', ensemble: 'small-group', offset: 115, minutes: 45 },
            { label: 'Closing remarks', kind: 'speech', ensemble: 'solo', offset: 160, minutes: 15 },
        ],
    },
];
/** Presenter colours. Chosen for separation at a glance in a dark backstage. */
export const PALETTE = [
    '#f2c14e', '#ef8a3c', '#e2564d', '#e0559b',
    '#a463d6', '#5b6ee0', '#3fb9b0', '#4bbf73',
];
/**
 * Minutes past midnight as HH:MM.
 *
 * 1440 renders as "24:00" rather than wrapping to "00:00": an end time at the
 * close of the day is the end, not the beginning, and wrapping made assignments
 * read as "00:00-00:00".
 */
export const minutesToHHMM = (m) => {
    const mins = Math.round(m);
    const h = Math.floor(mins / 60);
    return `${String(h).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
};
export const hhmmToMinutes = (s) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
    if (!m)
        return null;
    const h = Number(m[1]), mi = Number(m[2]);
    if (h > 23 || mi > 59)
        return null;
    return h * 60 + mi;
};
export const overlaps = (aS, aE, bS, bE) => aS < bE && bS < aE;
/**
 * Where a scheduled thing sits relative to a given moment.
 *
 * An unscheduled assignment is held for the whole show, so it is never "past":
 * it stays on that mic until someone takes it off. Ends are exclusive, so a
 * slot ending at 19:45 is already over at 19:45 and the next one may begin.
 */
export function liveStateOf(a, now) {
    if (!a.scheduled)
        return 'held';
    if (now < a.start)
        return 'upcoming';
    if (now >= a.end)
        return 'past';
    return 'live';
}
const LIVE_RANK = { live: 0, held: 1, upcoming: 2, past: 3 };
/** Sort a channel's assignments so that what matters now comes first. */
export function byRelevance(list, now) {
    return list.slice().sort((a, b) => {
        const d = LIVE_RANK[liveStateOf(a, now)] - LIVE_RANK[liveStateOf(b, now)];
        return d !== 0 ? d : a.start - b.start;
    });
}
export * from './alerts.js';
